import { Component, effect, inject, Input, OnInit, Signal, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLinkWithHref } from '@angular/router';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonInput,
  IonInputPasswordToggle,
  IonItem,
  IonLabel,
  IonList,
  IonRouterLinkWithHref,
  IonRow,
  LoadingController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoginLogoComponent, ScreensizeService } from '@okcargo-apps/layout-ionic';
import { StorageRepository } from '@okcargo-apps/shared-domain';
import { AppTab, ICredentials } from '@okcargo-apps/shared-domain-ionic';
import { APP_CONFIG, ConnectivityService } from '@okcargo-apps/shared-infrastructure';
import { AuthService } from '../auth';
@Component({
  selector: 'lib-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    IonRouterLinkWithHref,
    RouterLinkWithHref,
    IonLabel,
    IonGrid,
    IonRow,
    IonCol,
    IonButton,
    IonItem,
    IonList,
    IonInput,
    IonInputPasswordToggle,
    TranslateModule,
    LoginLogoComponent,
  ],
})
export class LoginFormComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly storageService = inject(StorageRepository);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly screensizeService = inject(ScreensizeService);
  private readonly translate = inject(TranslateService);
  private readonly loadingCtrl = inject(LoadingController);

  private readonly config = inject(APP_CONFIG);
  private readonly connectivity = inject(ConnectivityService);

  /** Logging in is a command: it cannot be done without connection. */
  protected readonly isOffline = this.connectivity.isOffline;

  @Input()
  token!: string;

  public readonly APP_NAME_TAGLINE = this.config.appNameTagline;

  public readonly LEGAL_LINKS = {
    termsUrl: this.config.termsUrl,
    privacyUrl: this.config.privacyUrl,
  };

  private _credentials: ICredentials;
  public loginForm: FormGroup;

  public isDesktopView: Signal<boolean> = this.screensizeService.isDesktopView;

  public errorMessage = signal<string | null>(null);

  constructor() {
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      rememberMe: true,
    });
    this._credentials = this.loginForm.value;

    // Clean errorMessage when form is updated
    effect(() => {
      this.loginForm.valueChanges.subscribe(() => {
        this.errorMessage.set(null);
      });
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.token) {
      this.doTokenValidation(this.token);
    }

    try {
      const { value } = await this.storageService.read('username');
      this.loginForm.patchValue({
        username: value,
      });
    } catch (error) {
      console.error(`Error: ${error}`);
    }
  }

  async doTokenValidation(token: string): Promise<void> {
    try {
      await this.authService.validateToken(token);
      // TODO move outside?
      this.router.navigate([`/tabs/${AppTab.DASHBOARD}`]);
    } catch (err) {
      this.errorMessage.set(this.translate.instant('login.error.wrong'));
      // console.error('Login failed', err);
    }
  }

  async doLogin(): Promise<void> {
    if (this.isOffline()) {
      return;
    }

    this._credentials = this.loginForm.value as ICredentials;

    if (this.loginForm.valid) {
      const loading = await this.loadingCtrl.create({
        message: this.translate.instant('general.send_data'),
      });
      await loading.present();
      try {
        await this.authService.login(this._credentials);
        this.errorMessage.set(null);
        // TODO move outside?
        this.router.navigate([`/tabs/${AppTab.DASHBOARD}`]);
      } catch (err) {
        this.errorMessage.set(this.translate.instant('login.error.wrong'));
        // console.error('Login failed', err);
      } finally {
        await loading.dismiss();
      }
    } else {
      this.loginForm.markAllAsTouched();
      this.errorMessage.set(this.translate.instant('login.error.invalid'));
    }
  }
}
